/**
 * Calendar View
 *
 * Task calendar with:
 * - Monthly/weekly/daily views
 * - Tasks positioned by due date
 * - Drag & drop to change dates
 * - Quick create task by clicking on day
 */

import { useMemo, useState, useCallback, type DragEvent } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Plus,
  LayoutGrid,
  Columns,
  Clock,
  Filter,
} from 'lucide-react';
import clsx from 'clsx';
import { format, isSameDay, startOfDay, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';
import { it } from 'date-fns/locale';

import type { Task, Priority } from '@/types';
import { useCalendar, type CalendarDay } from '@/hooks/useCalendar';
import { useViewData } from '@/hooks/useViewData';
import { useAppStore } from '@/store/appStore';
import { useTaskActions } from '@/hooks/useTaskActions';
import { isCompletedStatus, normalizeStatus } from '@/constants/taskStatuses';
import {
  ViewToolbar,
  ShowCompletedToggle,
  MultiFilterSelector,
} from '@/components/shared/view-toolbar';
import {
  StatusSelect,
} from '@/components/shared/inline-editors';
import { getAllFilterOptions } from '@/utils/filterOptions';
import { useProfilesContext } from '@/context/ProfilesContext';
import { handleError } from '@/utils/errorHandler';
import { TimeGrid } from '@/components/calendar';

// Day names for header
const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

export function CalendarView() {
  const {
    tasks,
    filters,
    viewMode: appViewMode,
    availableAssignees,
    onTaskClick,
    onFilterChange,
    onViewModeChange,
  } = useViewData();
  const { handleUpdateTask, handleCreateTask } = useTaskActions();

  const {
    currentDate,
    viewMode,
    calendarDays: _calendarDays,
    calendarWeeks,
    monthLabel,
    weekLabel,
    dayLabel,
    goToNext,
    goToPrev,
    goToToday,
    setViewMode,
    getTotalTasksInView,
  } = useCalendar({ tasks });

  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dragOverDate, setDragOverDate] = useState<Date | null>(null);
  const [quickCreateDate, setQuickCreateDate] = useState<Date | null>(null);

  // Filter options
  const uniqueValues = useMemo(() => {
    return {
      statuses: Array.from(new Set(tasks.map((t) => normalizeStatus(t.status)))).filter(Boolean),
      assignees: Array.from(
        new Set(tasks.flatMap((t) => (t.assignees || []).filter(Boolean)))
      ).filter((a): a is string => a !== null),
      tags: Array.from(new Set(tasks.flatMap((t) => (t.tags || []).filter(Boolean)))).filter(
        (t): t is string => t !== null
      ),
      priorities: Array.from(new Set(tasks.map((t) => t.priority).filter(Boolean))),
    };
  }, [tasks]);

  const { resolveDisplayName } = useProfilesContext();
  const filterOptions = useMemo(() => {
    return getAllFilterOptions(
      uniqueValues.statuses,
      uniqueValues.assignees,
      uniqueValues.tags,
      uniqueValues.priorities as Priority[],
      resolveDisplayName,
    );
  }, [uniqueValues, resolveDisplayName]);

  // Drag handlers
  const handleDragStart = useCallback((e: DragEvent, taskId: string) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedTaskId(null);
    setDragOverDate(null);
  }, []);

  const handleDragOver = useCallback((e: DragEvent, date: Date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(date);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverDate(null);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent, date: Date) => {
      e.preventDefault();
      const taskId = e.dataTransfer.getData('text/plain');

      if (taskId) {
        // Update task due date
        handleUpdateTask(taskId, { dueDate: startOfDay(date).getTime() });
      }

      setDraggedTaskId(null);
      setDragOverDate(null);
    },
    [handleUpdateTask]
  );

  // Quick create task
  const handleDayClick = useCallback(
    (date: Date, e: React.MouseEvent) => {
      // Only trigger if clicking on empty space, not on a task
      if ((e.target as HTMLElement).closest('.calendar-task')) return;
      setQuickCreateDate(date);
    },
    []
  );

  const handleQuickCreate = useCallback(
    async (name: string) => {
      if (!quickCreateDate || !name.trim()) return;

      try {
        await handleCreateTask({
          name: name.trim(),
          dueDate: startOfDay(quickCreateDate).getTime(),
        });
        setQuickCreateDate(null);
      } catch (error) {
        handleError(error, { level: 'error', message: 'Errore nella creazione del task dal calendario' });
      }
    },
    [quickCreateDate, handleCreateTask]
  );

  // Compute days arrays for TimeGrid (week and day views)
  const timeGridWeekDays = useMemo(() => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  }, [currentDate]);

  const timeGridDayDays = useMemo(() => [currentDate], [currentDate]);

  // Handler for TimeGrid task creation (includes scheduling)
  const handleTimeGridCreateTask = useCallback(
    async (taskData: Partial<Task>) => {
      try {
        await handleCreateTask(taskData);
      } catch (error) {
        handleError(error, { level: 'error', message: 'Errore nella creazione del task dal calendario' });
      }
    },
    [handleCreateTask]
  );

  // Get label based on view mode
  const currentLabel = viewMode === 'month' ? monthLabel : viewMode === 'week' ? weekLabel : dayLabel;

  return (
    <div className="flex-1 flex flex-col overflow-hidden animate-fade-in">
      {/* Toolbar */}
      <ViewToolbar
        onOpenSidebar={() => useAppStore.getState().setMobileSidebarOpen(true)}
        icon={<CalendarIcon className="w-4 h-4 text-accent-violet" />}
        title="Calendario"
        subtitle={`${getTotalTasksInView()} task`}
        viewMode={appViewMode}
        onViewModeChange={onViewModeChange}
        showViewSwitcher={!!onViewModeChange}
        leftContent={
          <div className="flex items-center gap-2 flex-wrap">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-surface-secondary rounded-lg border border-border-primary p-0.5">
              <button
                onClick={() => setViewMode('month')}
                className={clsx(
                  'flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium transition-all',
                  viewMode === 'month'
                    ? 'bg-surface-primary text-text-primary shadow-sm'
                    : 'text-text-tertiary hover:text-text-secondary'
                )}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                <span>Mese</span>
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={clsx(
                  'flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium transition-all',
                  viewMode === 'week'
                    ? 'bg-surface-primary text-text-primary shadow-sm'
                    : 'text-text-tertiary hover:text-text-secondary'
                )}
              >
                <Columns className="w-3.5 h-3.5" />
                <span>Settimana</span>
              </button>
              <button
                onClick={() => setViewMode('day')}
                className={clsx(
                  'flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium transition-all',
                  viewMode === 'day'
                    ? 'bg-surface-primary text-text-primary shadow-sm'
                    : 'text-text-tertiary hover:text-text-secondary'
                )}
              >
                <Clock className="w-3.5 h-3.5" />
                <span>Giorno</span>
              </button>
            </div>

            {/* Filters */}
            <MultiFilterSelector
              filters={{
                status: filters.status || [],
                assignees: filters.assignees || [],
                tags: filters.tags || [],
                priority: filters.priority || [],
              }}
              onFilterChange={onFilterChange}
              availableOptions={filterOptions}
              icon={<Filter className="w-3.5 h-3.5 text-text-tertiary" />}
            />

            <ShowCompletedToggle
              showCompleted={filters.showCompleted}
              onToggle={(show) => onFilterChange({ showCompleted: show })}
            />
          </div>
        }
        rightContent={
          <div className="flex items-center flex-wrap justify-end gap-2">
            {/* Today button */}
            <button
              onClick={goToToday}
              className="px-3 py-1.5 rounded-lg text-[11px] font-medium text-accent-violet bg-accent-violet/10 hover:bg-accent-violet/20 transition-colors"
            >
              Oggi
            </button>

            {/* Navigation */}
            <div className="flex items-center gap-1 bg-surface-secondary rounded-lg border border-border-primary">
              <button
                onClick={goToPrev}
                className="p-1.5 hover:bg-surface-hover rounded-l-lg transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-text-tertiary" />
              </button>
              <span className="px-3 py-1 text-sm font-medium text-text-primary min-w-[180px] text-center">
                {currentLabel}
              </span>
              <button
                onClick={goToNext}
                className="p-1.5 hover:bg-surface-hover rounded-r-lg transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-text-tertiary" />
              </button>
            </div>
          </div>
        }
      />

      {/* Calendar Content */}
      <div className="flex-1 overflow-auto p-4">
        {viewMode === 'month' && (
          <MonthView
            weeks={calendarWeeks}
            draggedTaskId={draggedTaskId}
            dragOverDate={dragOverDate}
            quickCreateDate={quickCreateDate}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onDayClick={handleDayClick}
            onTaskClick={onTaskClick}
            onUpdateTask={handleUpdateTask}
            onQuickCreate={handleQuickCreate}
            onCancelQuickCreate={() => setQuickCreateDate(null)}
            availableAssignees={availableAssignees}
          />
        )}
        {viewMode === 'week' && (
          <TimeGrid
            tasks={tasks}
            days={timeGridWeekDays}
            onUpdateTask={handleUpdateTask}
            onCreateTask={handleTimeGridCreateTask}
            onTaskClick={onTaskClick}
          />
        )}
        {viewMode === 'day' && (
          <TimeGrid
            tasks={tasks}
            days={timeGridDayDays}
            onUpdateTask={handleUpdateTask}
            onCreateTask={handleTimeGridCreateTask}
            onTaskClick={onTaskClick}
          />
        )}
      </div>
    </div>
  );
}

// Month View Component
interface MonthViewProps {
  weeks: { days: CalendarDay[]; weekNumber: number }[];
  draggedTaskId: string | null;
  dragOverDate: Date | null;
  quickCreateDate: Date | null;
  onDragStart: (e: DragEvent, taskId: string) => void;
  onDragEnd: () => void;
  onDragOver: (e: DragEvent, date: Date) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent, date: Date) => void;
  onDayClick: (date: Date, e: React.MouseEvent) => void;
  onTaskClick?: (task: Task) => void;
  onUpdateTask?: (taskId: string, updates: Partial<Task>) => void;
  onQuickCreate: (name: string) => void;
  onCancelQuickCreate: () => void;
  availableAssignees?: string[];
}

function MonthView({
  weeks,
  draggedTaskId,
  dragOverDate,
  quickCreateDate,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onDayClick,
  onTaskClick,
  onUpdateTask,
  onQuickCreate,
  onCancelQuickCreate,
  availableAssignees,
}: MonthViewProps) {
  return (
    <div className="h-full flex flex-col bg-surface-secondary/30 rounded-xl border border-border-primary overflow-hidden">
      {/* Day Names Header */}
      <div className="grid grid-cols-7 border-b border-border-primary">
        {dayNames.map((name, index) => (
          <div
            key={name}
            className={clsx(
              'px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide',
              index >= 5 ? 'text-text-quaternary' : 'text-text-tertiary'
            )}
          >
            {name}
          </div>
        ))}
      </div>

      {/* Weeks */}
      <div className="flex-1 grid" style={{ gridTemplateRows: `repeat(${weeks.length}, 1fr)` }}>
        {weeks.map((week) => (
          <div key={week.weekNumber} className="grid grid-cols-7 border-b border-border-primary last:border-b-0">
            {week.days.map((day) => (
              <CalendarCell
                key={day.date.toISOString()}
                day={day}
                isDragOver={dragOverDate ? isSameDay(dragOverDate, day.date) : false}
                showQuickCreate={quickCreateDate ? isSameDay(quickCreateDate, day.date) : false}
                onDragOver={(e) => onDragOver(e, day.date)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, day.date)}
                onClick={(e) => onDayClick(day.date, e)}
                onTaskClick={onTaskClick}
                onUpdateTask={onUpdateTask}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                draggedTaskId={draggedTaskId}
                onQuickCreate={onQuickCreate}
                onCancelQuickCreate={onCancelQuickCreate}
                availableAssignees={availableAssignees}
                compact
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// Week View Component
interface WeekViewProps {
  days: CalendarDay[];
  draggedTaskId: string | null;
  dragOverDate: Date | null;
  quickCreateDate: Date | null;
  onDragStart: (e: DragEvent, taskId: string) => void;
  onDragEnd: () => void;
  onDragOver: (e: DragEvent, date: Date) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent, date: Date) => void;
  onDayClick: (date: Date, e: React.MouseEvent) => void;
  onTaskClick?: (task: Task) => void;
  onUpdateTask?: (taskId: string, updates: Partial<Task>) => void;
  onQuickCreate: (name: string) => void;
  onCancelQuickCreate: () => void;
  availableAssignees?: string[];
}

function _WeekView({
  days,
  draggedTaskId,
  dragOverDate,
  quickCreateDate,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onDayClick,
  onTaskClick,
  onUpdateTask,
  onQuickCreate,
  onCancelQuickCreate,
  availableAssignees,
}: WeekViewProps) {
  return (
    <div className="h-full flex gap-2">
      {days.slice(0, 7).map((day) => (
        <div
          key={day.date.toISOString()}
          className="flex-1 flex flex-col bg-surface-secondary/30 rounded-xl border border-border-primary overflow-hidden"
        >
          {/* Day Header */}
          <div
            className={clsx(
              'px-3 py-2 border-b border-border-primary text-center',
              day.isToday && 'bg-accent-violet/10'
            )}
          >
            <div className="text-[10px] text-text-tertiary uppercase">
              {format(day.date, 'EEE', { locale: it })}
            </div>
            <div
              className={clsx(
                'text-lg font-semibold',
                day.isToday ? 'text-accent-violet' : 'text-text-primary'
              )}
            >
              {format(day.date, 'd')}
            </div>
          </div>

          {/* Day Content */}
          <CalendarCell
            day={day}
            isDragOver={dragOverDate ? isSameDay(dragOverDate, day.date) : false}
            showQuickCreate={quickCreateDate ? isSameDay(quickCreateDate, day.date) : false}
            onDragOver={(e) => onDragOver(e, day.date)}
            onDragLeave={onDragLeave}
            onDrop={(e) => onDrop(e, day.date)}
            onClick={(e) => onDayClick(day.date, e)}
            onTaskClick={onTaskClick}
            onUpdateTask={onUpdateTask}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            draggedTaskId={draggedTaskId}
            onQuickCreate={onQuickCreate}
            onCancelQuickCreate={onCancelQuickCreate}
            availableAssignees={availableAssignees}
            compact={false}
            hideDate
          />
        </div>
      ))}
    </div>
  );
}

// Day View Component
interface DayViewProps {
  day: CalendarDay | undefined;
  draggedTaskId: string | null;
  dragOverDate: Date | null;
  quickCreateDate: Date | null;
  onDragStart: (e: DragEvent, taskId: string) => void;
  onDragEnd: () => void;
  onDragOver: (e: DragEvent, date: Date) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent, date: Date) => void;
  onDayClick: (date: Date, e: React.MouseEvent) => void;
  onTaskClick?: (task: Task) => void;
  onUpdateTask?: (taskId: string, updates: Partial<Task>) => void;
  onQuickCreate: (name: string) => void;
  onCancelQuickCreate: () => void;
  availableAssignees?: string[];
}

function _DayView({
  day,
  draggedTaskId,
  dragOverDate,
  quickCreateDate,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onDayClick,
  onTaskClick,
  onUpdateTask,
  onQuickCreate,
  onCancelQuickCreate,
  availableAssignees,
}: DayViewProps) {
  if (!day) {
    return (
      <div className="h-full flex items-center justify-center text-text-tertiary">
        Nessun giorno selezionato
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-surface-secondary/30 rounded-xl border border-border-primary overflow-hidden">
      {/* Day Header */}
      <div
        className={clsx(
          'px-4 py-3 border-b border-border-primary',
          day.isToday && 'bg-accent-violet/10'
        )}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] text-text-tertiary uppercase">
              {format(day.date, 'EEEE', { locale: it })}
            </div>
            <div
              className={clsx(
                'text-2xl font-semibold',
                day.isToday ? 'text-accent-violet' : 'text-text-primary'
              )}
            >
              {format(day.date, 'd MMMM yyyy', { locale: it })}
            </div>
          </div>
          <div className="text-sm text-text-tertiary">{day.tasks.length} task</div>
        </div>
      </div>

      {/* Day Content */}
      <CalendarCell
        day={day}
        isDragOver={dragOverDate ? isSameDay(dragOverDate, day.date) : false}
        showQuickCreate={quickCreateDate ? isSameDay(quickCreateDate, day.date) : false}
        onDragOver={(e) => onDragOver(e, day.date)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, day.date)}
        onClick={(e) => onDayClick(day.date, e)}
        onTaskClick={onTaskClick}
        onUpdateTask={onUpdateTask}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        draggedTaskId={draggedTaskId}
        onQuickCreate={onQuickCreate}
        onCancelQuickCreate={onCancelQuickCreate}
        availableAssignees={availableAssignees}
        compact={false}
        hideDate
        expanded
      />
    </div>
  );
}

// Calendar Cell Component
interface CalendarCellProps {
  day: CalendarDay;
  isDragOver: boolean;
  showQuickCreate: boolean;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent) => void;
  onClick: (e: React.MouseEvent) => void;
  onTaskClick?: (task: Task) => void;
  onUpdateTask?: (taskId: string, updates: Partial<Task>) => void;
  onDragStart: (e: DragEvent, taskId: string) => void;
  onDragEnd: () => void;
  draggedTaskId: string | null;
  onQuickCreate: (name: string) => void;
  onCancelQuickCreate: () => void;
  availableAssignees?: string[];
  compact?: boolean;
  hideDate?: boolean;
  expanded?: boolean;
}

function CalendarCell({
  day,
  isDragOver,
  showQuickCreate,
  onDragOver,
  onDragLeave,
  onDrop,
  onClick,
  onTaskClick,
  onUpdateTask,
  onDragStart,
  onDragEnd,
  draggedTaskId,
  onQuickCreate,
  onCancelQuickCreate,
  availableAssignees,
  compact = false,
  hideDate = false,
  expanded = false,
}: CalendarCellProps) {
  const [quickCreateValue, setQuickCreateValue] = useState('');

  const handleQuickCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onQuickCreate(quickCreateValue);
    setQuickCreateValue('');
  };

  const handleQuickCreateKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancelQuickCreate();
      setQuickCreateValue('');
    }
  };

  const maxVisibleTasks = compact ? 3 : expanded ? 100 : 5;
  const visibleTasks = day.tasks.slice(0, maxVisibleTasks);
  const hiddenCount = day.tasks.length - visibleTasks.length;

  return (
    <div
      className={clsx(
        'flex-1 p-1 border-r border-border-primary last:border-r-0 transition-all min-h-[80px]',
        !day.isCurrentMonth && 'bg-surface-tertiary/20',
        day.isWeekend && 'bg-surface-tertiary/10',
        isDragOver && 'bg-accent-violet/10 ring-2 ring-inset ring-accent-violet/30',
        expanded && 'overflow-y-auto'
      )}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={onClick}
    >
      {/* Date Number */}
      {!hideDate && (
        <div
          className={clsx(
            'flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-medium mb-1',
            day.isToday
              ? 'bg-accent-violet text-white'
              : !day.isCurrentMonth
                ? 'text-text-quaternary'
                : 'text-text-secondary'
          )}
        >
          {format(day.date, 'd')}
        </div>
      )}

      {/* Quick Create Input */}
      {showQuickCreate && (
        <form onSubmit={handleQuickCreateSubmit} className="mb-1">
          <input
            type="text"
            value={quickCreateValue}
            onChange={(e) => setQuickCreateValue(e.target.value)}
            onKeyDown={handleQuickCreateKeyDown}
            placeholder="Nuovo task..."
            className="w-full px-2 py-1 text-[11px] rounded bg-surface-primary border border-accent-violet focus:outline-none"
            autoFocus
          />
        </form>
      )}

      {/* Tasks */}
      <div className="space-y-1">
        {visibleTasks.map((task) => (
          <CalendarTaskItem
            key={task.id}
            task={task}
            compact={compact}
            isDragging={draggedTaskId === task.id}
            onDragStart={(e) => onDragStart(e, task.id)}
            onDragEnd={onDragEnd}
            onClick={() => onTaskClick?.(task)}
            onUpdate={onUpdateTask}
            availableAssignees={availableAssignees}
          />
        ))}

        {hiddenCount > 0 && (
          <div className="text-[10px] text-text-tertiary text-center py-0.5">
            +{hiddenCount} altri
          </div>
        )}
      </div>

      {/* Add Task Button (non-compact) */}
      {!compact && day.tasks.length === 0 && !showQuickCreate && (
        <button className="w-full flex items-center justify-center gap-1 py-2 text-[10px] text-text-quaternary hover:text-accent-violet transition-colors">
          <Plus className="w-3 h-3" />
          <span>Aggiungi task</span>
        </button>
      )}
    </div>
  );
}

// Calendar Task Item
interface CalendarTaskItemProps {
  task: Task;
  compact?: boolean;
  isDragging?: boolean;
  onDragStart: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onClick?: () => void;
  onUpdate?: (taskId: string, updates: Partial<Task>) => void;
  availableAssignees?: string[];
}

function CalendarTaskItem({
  task,
  compact = false,
  isDragging,
  onDragStart,
  onDragEnd,
  onClick,
  onUpdate,
}: CalendarTaskItemProps) {
  const isCompleted = isCompletedStatus(task.status);
  const priorityColors: Record<string, string> = {
    urgent: 'border-l-accent-rose',
    high: 'border-l-accent-amber',
    normal: 'border-l-accent-blue',
    low: 'border-l-accent-emerald',
  };

  const handleUpdate = (updates: Partial<Task>) => {
    if (onUpdate) {
      onUpdate(task.id, updates);
    }
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className={clsx(
        'calendar-task px-2 py-1 rounded text-[10px] font-medium cursor-grab active:cursor-grabbing transition-all border-l-2',
        isCompleted
          ? 'bg-surface-tertiary/50 text-text-tertiary line-through'
          : 'bg-surface-primary text-text-primary hover:shadow-sm',
        task.priority ? priorityColors[task.priority] : 'border-l-border-primary',
        isDragging && 'opacity-50 scale-95'
      )}
    >
      <div className="flex items-center gap-1">
        {!compact && onUpdate && (
          <div onClick={(e) => e.stopPropagation()}>
            <StatusSelect
              value={task.status}
              onChange={(status) => handleUpdate({ status })}
              size="sm"
              showLabel={false}
            />
          </div>
        )}
        <span className="flex-1 truncate">{task.name}</span>
        {!compact && task.assignees && task.assignees.length > 0 && (
          <div className="flex -space-x-1">
            {task.assignees.slice(0, 2).map((assignee) => (
              <div
                key={assignee}
                className="w-4 h-4 rounded-full bg-accent-violet/20 flex items-center justify-center text-[8px] font-semibold text-accent-violet border border-surface-primary"
                title={assignee}
              >
                {assignee.charAt(0).toUpperCase()}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
